import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Event as EMEvent } from 'app/models/event.model';
import { LOVConferenceProvince } from 'app/models/lov-conference-province.model';
import { Lov } from 'app/models/lov.model';
import { OnlineConferenceContent } from 'app/models/online-conference-content.model';
import { VwDCSLocalTombstoneInfo } from 'app/models/vw-d-c-s-local-tombstone-info.model';
import { BaseService, PhysicalAddress } from '@mrm/core';
import { ConferenceRegistration } from './../models/conference-registration.model';
import { RegistrationAccessibilityNeedType } from 'app/models/registration-accessibility-need-type.model';

@Injectable({
  providedIn: 'root'
})
export class ConferenceRegistrationService extends BaseService {
  private _conferenceRegistrationURL = 'EM/ConferenceOnlineRegistration/';

  constructor(private _http: HttpClient) {
    super();
  }

  getEvent(id: string) {
    return this._http.get<EMEvent>(this._conferenceRegistrationURL + 'GetEvent/' + id);
  }

  getContent(id: string) {
    return this._http.get<OnlineConferenceContent[]>(this._conferenceRegistrationURL + 'GetContent/' + id);
  }

  getLocalAddress(localOrgNumber: string) {
    return this._http.get<PhysicalAddress>(this._conferenceRegistrationURL + 'GetLocalAddress/' + localOrgNumber);
  }

  getLocalInformation(localOrgNumber: string, isHeu: boolean) {
    return this._http.get<VwDCSLocalTombstoneInfo>(this._conferenceRegistrationURL + 'GetLocalInformation/' + localOrgNumber + '/' + isHeu);
  }

  getConferenceLanguages(id: string) {
    return this._http.get<any[]>(this._conferenceRegistrationURL + 'GetConferenceLanguages/' + id);
  }

  getConferenceGenders(id: string) {
    return this._http.get<any[]>(this._conferenceRegistrationURL + 'GetConferenceGenders/' + id);
  }

  getConferenceExpertises(id: string) {
    return this._http.get<any[]>(this._conferenceRegistrationURL + 'GetConferenceExpertises/' + id);
  }

  getConferencePreferences(id: string) {
    return this._http.get<any[]>(this._conferenceRegistrationURL + 'GetConferencePreferences/' + id);
  }

  getConferenceDesignations(id: string) {
    return this._http.get<any[]>(this._conferenceRegistrationURL + 'GetConferenceDesignations/' + id);
  }

  getConferenceSpecialNeeds(id: string) {
    return this._http.get<any[]>(this._conferenceRegistrationURL + 'GetConferenceSpecialNeeds/' + id);
  }

  getConferenceNoteTypes(id: string) {
    return this._http.get<any[]>(this._conferenceRegistrationURL + 'GetConferenceNoteTypes/' + id);
  }

  getConferenceRegistrationTypes(id: string) {
    return this._http.get<any[]>(this._conferenceRegistrationURL + 'GetConferenceRegistrationTypes/' + id);
  }

  getLuiSectors(id: string) {
    return this._http.get<any[]>(this._conferenceRegistrationURL + 'GetLuiSectors/' + id);
  }

  getConferenceRegions(id: string) {
    return this._http.get<any[]>(this._conferenceRegistrationURL + 'GetConferenceRegions/' + id);
  }

  getConferenceProvinces(id: string) {
    return this._http.get<LOVConferenceProvince[]>(this._conferenceRegistrationURL + 'GetConferenceProvinces/' + id);
  }

  getConferenceContentTypes(id: string) {
    return this._http.get<any[]>(this._conferenceRegistrationURL + 'GetConferenceContentTypes/' + id);
  }

  getConferenceCorrespondenceLanguages(id: string) {
    return this._http.get<any[]>(this._conferenceRegistrationURL + 'GetConferenceCorrespondenceLanguages/' + id);
  }

  getConferenceSectors(id: string) {
    return this._http.get<any[]>(this._conferenceRegistrationURL + 'GetConferenceSectors/' + id);
  }

  getConferenceOccupationCodes(id: string) {
    return this._http.get<any[]>(this._conferenceRegistrationURL + 'GetConferenceOccupationCodes/' + id);
  }

  getConferenceJurisdictions(id: string) {
    return this._http.get<any[]>(this._conferenceRegistrationURL + 'GetConferenceJurisdictions/' + id);
  }

  getLookups() {
    return this._http.get<Lov>(this._conferenceRegistrationURL + 'GetLookups');
  }

  save(registration: ConferenceRegistration) {
    return this._http.post<number>(this._conferenceRegistrationURL + 'Save', registration);
  }

  saveSecondStep(registration: ConferenceRegistration) {
    return this._http.post<number>(this._conferenceRegistrationURL + 'SaveSecondStep', registration);
  }

  getRegistration(id: string) {
    return this._http.get<ConferenceRegistration>(this._conferenceRegistrationURL + 'GetConferenceRegistration/' + id);
  }

  sendConfirmationNotification(registration: ConferenceRegistration) {
    return this._http.post(this._conferenceRegistrationURL + 'SendConfirmationNotification', registration);
  }

  isUniqueEmail(conventionDBID: number, emailAddress: string) {
    return this._http.get<boolean>(this._conferenceRegistrationURL + 'IsUniqueEmail/' + conventionDBID + '/' + emailAddress);
  }

  validateRegistrationsForPayment(conferenceDBID: number, ids: string[]) {
    return this._http.get(this._conferenceRegistrationURL + 'ValidateRegistrationsForPayment', { params: { conferenceDBID: conferenceDBID, conferenceRegistrationDBIDs: ids } });
  }

  getFundingSource() {
    return this._http.get<string>(this._conferenceRegistrationURL + 'GetFundingSource');
  }

  isWithinWorkshopCap(registration: ConferenceRegistration, keepNavProp?: string[]) {
    return this._http.post<number[]>(this._conferenceRegistrationURL + 'IsWithinWorkshopCap', this.removeNavigationalProperties(registration, keepNavProp));
  }

  assignWorkshopRooms(conferenceDBID: number, conferenceRegistrationDBID: number) {
    return this._http.post(this._conferenceRegistrationURL + `AutomaticallyAssignWorkshops/${conferenceDBID}/${conferenceRegistrationDBID}`, null);
  }

  getEventConferences() {
    return this._http.get<EMEvent[]>(this._conferenceRegistrationURL + 'GetEventConferences');
  }

  getConferenceAccesssibilityNeeds(conferenceDBID: number) {
    return this._http.get<RegistrationAccessibilityNeedType[]>(this._conferenceRegistrationURL + `GetConferenceAccessibilityNeeds/${conferenceDBID}/${this.getCurrentCulture()}`);
  }
}
